import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import {
  ReturnResolution,
  ReturnStatus,
  type Order,
  type OrderSummary,
  type Return,
} from '@nucleus-commerce/api-client/admin';

import { orderListQueryOptions, orderQueryOptions } from '@/features/orders';

/** Fields GET /returns sorts on; anything else is a 400. */
export const RETURN_SORTS = ['requestedAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const RETURN_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(ReturnStatus) },
  { key: 'resolution', type: 'enum', options: valuesOf(ReturnResolution) },
  { key: 'requestedAt', type: 'date-range' },
  { key: 'orderId', type: 'async' },
] as const;

const orderOption = (order: Order | OrderSummary): FilterOption => ({
  value: order.id,
  label: order.name,
  description: order.email ?? undefined,
});

export interface ReturnFiltersOptions {
  /** The `returns` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of the returns table, in the order the filter menu lists them. */
export function returnFilters({
  t,
  queryClient,
  storeId,
}: ReturnFiltersOptions): Array<FilterDef<Return>> {
  const options = (values: Record<string, string>, prefix: string): FilterOption[] =>
    valuesOf(values).map(({ value }) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: options(ReturnStatus, 'status'),
    },
    {
      key: 'resolution',
      label: t('filters.resolution'),
      type: 'enum',
      pinned: true,
      options: options(ReturnResolution, 'resolution'),
    },
    {
      key: 'requestedAt',
      label: t('filters.requestedAt'),
      type: 'date-range',
      aliases: ['requested'],
    },
    {
      key: 'orderId',
      label: t('filters.order'),
      type: 'async',
      // The endpoint takes one order.
      multiple: false,
      aliases: ['order'],
      ...queryFilterOptions(queryClient, {
        search: (q) => orderListQueryOptions(storeId, { ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(orderOption),
        resolve: {
          query: ([id]) => orderQueryOptions(storeId, id ?? ''),
          toOptions: (order) => [orderOption(order)],
        },
      }),
    },
  ];
}
